(() => {
  if (customElements.get('chastar-card-gallery')) return;
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
  const mobileControls = matchMedia('(max-width: 749px), (hover: none), (pointer: coarse)');
  const activeCounters = new WeakMap();
  const photoKey = source => {
    try { return new URL(source, location.href).pathname; } catch { return source; }
  };
  customElements.define('chastar-card-gallery', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.viewport = this.querySelector('[data-cpg-viewport]') || this.querySelector('.cc-card__media, .media');
      this.primary = this.viewport?.querySelector('img');
      const data = this.querySelector('[data-cpg-images]');
      if (!this.primary || !data) return;
      try { this.photos = JSON.parse(data.textContent); } catch { return; }
      if (!Array.isArray(this.photos) || this.photos.length < 2) return;
      this.photos = this.photos.filter(photo => photo && typeof photo.src === 'string');
      if (this.photos.length < 2) return;
      this.controller = new AbortController();
      this.loading = false;
      this.cache = new Map();
      this.index = 0;
      this.queuedStep = 0;
      this.status = this.querySelector('[data-cpg-status]');
      this.position = this.querySelector('[data-cpg-position]');
      this.card = this.closest('[data-cc-card], .product-card-wrapper');
      this.counterScope = this.closest('.cc-products__grid, .product-grid, [data-wishlist-grid], [data-cf-track]')
        || this.closest('.shopify-section') || this.parentElement;
      const options = { signal: this.controller.signal };
      this.addEventListener('pointerdown', event => this.start(event), options);
      this.addEventListener('dragstart', event => event.preventDefault(), options);
      this.addEventListener('click', event => {
        if (this.viewport.contains(event.target) && event.detail !== 0 && performance.now() < this.suppressClickUntil) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      }, { ...options, capture: true });
      this.querySelectorAll('[data-cpg-step]').forEach(button => button.addEventListener('click', event => {
        event.preventDefault();
        event.stopPropagation();
        this.go(Number(button.dataset.cpgStep));
      }, options));
      this.addEventListener('keydown', event => {
        if (event.altKey || event.ctrlKey || event.metaKey) return;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          event.stopPropagation();
          this.go(event.key === 'ArrowLeft' ? -1 : 1);
        } else if (event.key === 'Home' || event.key === 'End') {
          event.preventDefault();
          this.cancelPending();
          this.queuedStep = 0;
          this.finishAnimation();
          this.activate(false);
          this.index = event.key === 'Home' ? 0 : this.photos.length - 1;
          this.renderStage();
          this.commit(true);
        }
      }, options);
      this.addEventListener('pointerenter', event => {
        if (event.pointerType !== 'mouse') return;
        const index = this.visibleIndex();
        this.loadPhoto(this.wrap(index - 1));
        this.loadPhoto(this.wrap(index + 1));
      }, options);
      this.addEventListener('pointerleave', () => {
        if (this.drag || this.animation || this.loading) return;
        this.deactivate();
      }, options);
      this.sourceObserver = new MutationObserver(() => this.syncSource());
      this.sourceObserver.observe(this.primary, { attributes: true, attributeFilter: ['src'] });
      this.resizeObserver = new ResizeObserver(() => {
        if (this.drag) this.end(null, true);
        this.finishAnimation();
      });
      this.resizeObserver.observe(this.viewport);
      this.syncSource();
      this.updatePosition();
      this.setAttribute('data-cpg-ready', '');
      this.querySelectorAll('[data-cpg-controls]').forEach(element => { element.hidden = false; });
    }
    wrap(index) { return (index % this.photos.length + this.photos.length) % this.photos.length; }
    showCounter() {
      if (!mobileControls.matches) return;
      clearTimeout(this.counterTimer);
      const previous = activeCounters.get(this.counterScope);
      if (previous !== this) previous?.hideCounter();
      activeCounters.set(this.counterScope, this);
      this.setAttribute('data-cpg-counter-active', '');
    }
    hideCounter() {
      clearTimeout(this.counterTimer);
      this.counterTimer = null;
      this.removeAttribute('data-cpg-counter-active');
      if (activeCounters.get(this.counterScope) === this) activeCounters.delete(this.counterScope);
    }
    visibleIndex() {
      if (this.hasAttribute('data-cpg-engaged')) return this.index;
      const secondary = this.viewport.querySelector('.cc-card__image--secondary, .media--hover-effect > img + img');
      if (secondary?.naturalWidth && getComputedStyle(secondary).opacity > .5 && getComputedStyle(secondary).visibility !== 'hidden') {
        const match = this.photos.findIndex(photo => photoKey(photo.src) === photoKey(secondary.getAttribute('src')));
        if (match !== -1) return match;
      }
      return this.index;
    }
    loadPhoto(index) {
      if (this.cache.has(index)) return this.cache.get(index);
      const image = new Image();
      const photo = this.photos[index];
      image.alt = photo.alt || '';
      image.decoding = 'async';
      image.draggable = false;
      image.className = 'cpg__photo';
      image.sizes = this.primary.sizes;
      image.width = Number(photo.width) || this.primary.width;
      image.height = Number(photo.height) || this.primary.height;
      const record = { image, ready: false };
      record.promise = new Promise(resolve => {
        let settled = false;
        const finish = success => {
          if (settled) return;
          settled = true;
          clearTimeout(record.timeout);
          record.ready = success;
          image.onload = image.onerror = null;
          resolve(success);
        };
        image.onload = () => finish(image.naturalWidth > 0);
        image.onerror = () => finish(false);
        record.cancel = () => finish(false);
        record.timeout = setTimeout(() => finish(false), 10000);
        if (photo.srcset) image.srcset = photo.srcset;
        image.src = photo.src;
        if (image.complete) finish(image.naturalWidth > 0);
      });
      this.cache.set(index, record);
      return record;
    }
    activate(useHover = true) {
      if (useHover) this.index = this.visibleIndex();
      if (!this.track) {
        this.track = document.createElement('div');
        this.track.className = 'cpg__track';
        this.track.setAttribute('aria-hidden', 'true');
        this.viewport.append(this.track);
      }
      this.setAttribute('data-cpg-engaged', '');
      this.renderStage();
    }
    renderStage() {
      if (!this.track) return;
      this.track.style.transition = 'none';
      this.track.style.transform = 'translate3d(0, 0, 0)';
      const frames = [-1, 0, 1].map(offset => {
        const frame = document.createElement('div');
        frame.className = 'cpg__frame';
        frame.style.left = `${offset * 100}%`;
        const record = this.loadPhoto(this.wrap(this.index + offset));
        const image = record.image.cloneNode();
        frame.append(image);
        return frame;
      });
      this.track.replaceChildren(...frames);
      this.updatePosition();
    }
    updatePosition(announce = false) {
      if (this.position) this.position.textContent = `${this.index + 1} / ${this.photos.length}`;
      this.dataset.cpgIndex = String(this.index);
      if (announce && this.status) {
        this.status.textContent = (this.dataset.positionLabel || '{{ current }} / {{ total }}')
          .replace('__CURRENT__', String(this.index + 1)).replace('__TOTAL__', String(this.photos.length));
      }
    }
    start(event) {
      if (!event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0) || !this.viewport.contains(event.target)) return;
      if (this.drag) return;
      this.cancelPending();
      this.queuedStep = 0;
      this.finishAnimation();
      this.suppressClickUntil = 0;
      const index = this.visibleIndex();
      this.loadPhoto(this.wrap(index - 1));
      this.loadPhoto(this.wrap(index + 1));
      this.drag = { id: event.pointerId, x: event.clientX, y: event.clientY, lastX: event.clientX, lastTime: event.timeStamp, velocity: 0, offset: 0, active: false };
      this.pointerController = new AbortController();
      const options = { signal: this.pointerController.signal };
      window.addEventListener('pointermove', move => this.move(move), { ...options, passive: false });
      window.addEventListener('pointerup', up => this.end(up), options);
      window.addEventListener('pointercancel', cancel => this.end(cancel, true), options);
      window.addEventListener('blur', () => this.end(null, true), options);
      this.viewport.addEventListener('lostpointercapture', lost => { if (lost.target === this.viewport) this.end(lost, true); }, options);
    }
    move(event) {
      const drag = this.drag;
      if (!drag || event.pointerId !== drag.id) return;
      let offset = event.clientX - drag.x;
      const vertical = event.clientY - drag.y;
      if (!drag.active) {
        if (Math.max(Math.abs(offset), Math.abs(vertical)) < 7) return;
        if (Math.abs(vertical) > Math.abs(offset)) { this.end(event, true); return; }
        drag.active = true;
        this.activate();
        this.showCounter();
        this.setAttribute('data-cpg-dragging', '');
        this.viewport.setPointerCapture?.(drag.id);
      }
      if (event.cancelable) event.preventDefault();
      event.stopPropagation();
      const width = this.viewport.getBoundingClientRect().width;
      if (!width) return;
      const elapsed = event.timeStamp - drag.lastTime;
      if (elapsed > 0) drag.velocity = (event.clientX - drag.lastX) / elapsed;
      drag.lastX = event.clientX;
      drag.lastTime = event.timeStamp;
      while (offset <= -width) { this.index = this.wrap(this.index + 1); drag.x -= width; offset += width; this.renderStage(); }
      while (offset >= width) { this.index = this.wrap(this.index - 1); drag.x += width; offset -= width; this.renderStage(); }
      drag.offset = offset;
      this.track.style.transform = `translate3d(${offset}px, 0, 0)`;
    }
    end(event, cancelled = false) {
      const drag = this.drag;
      if (!drag || (event && event.pointerId !== drag.id)) return;
      this.drag = null;
      this.pointerController?.abort();
      this.pointerController = null;
      if (this.viewport.hasPointerCapture?.(drag.id)) this.viewport.releasePointerCapture(drag.id);
      this.removeAttribute('data-cpg-dragging');
      if (!drag.active) return;
      if (this.hasAttribute('data-cpg-counter-active')) {
        this.counterTimer = setTimeout(() => this.hideCounter(), 4000);
      }
      this.suppressClickUntil = performance.now() + 450;
      const width = this.viewport.getBoundingClientRect().width;
      const velocity = event && event.timeStamp - drag.lastTime > 90 ? 0 : drag.velocity;
      const advance = !cancelled && (Math.abs(drag.offset) >= width * .22 || (Math.abs(drag.offset) >= 16 && Math.abs(velocity) > .45));
      const step = advance ? (drag.offset < 0 ? 1 : -1) : 0;
      this.settle(this.wrap(this.index + step), step ? -step * width : 0);
    }
    go(step) {
      if (!step) return;
      if (this.animation || this.loading) { this.queuedStep += step; return; }
      this.activate();
      const index = this.wrap(this.index + step);
      this.settle(index, -Math.sign(step) * this.viewport.getBoundingClientRect().width);
    }
    async settle(index, offset) {
      const request = {};
      this.pendingSettle = request;
      const photo = this.loadPhoto(index);
      if (!photo.ready) {
        this.loading = true;
        this.setAttribute('aria-busy', 'true');
        const loaded = await photo.promise;
        if (this.pendingSettle !== request || !this.controller || !this.isConnected) return;
        this.loading = false;
        this.removeAttribute('aria-busy');
        if (!loaded) {
          this.queuedStep = 0;
          this.renderStage();
          this.commit();
          if (this.status) this.status.textContent = this.dataset.errorLabel || '';
          return;
        }
      }
      this.pendingSettle = null;
      const duration = reducedMotion.matches ? 0 : 230;
      if (!duration) { this.index = index; this.renderStage(); this.commit(true); this.processQueue(); return; }
      this.animation = { index };
      this.track.getBoundingClientRect();
      this.track.style.transition = `transform ${duration}ms cubic-bezier(.22,1,.36,1)`;
      this.track.style.transform = `translate3d(${offset}px, 0, 0)`;
      this.animation.timer = setTimeout(() => this.finishAnimation(), duration + 30);
    }
    finishAnimation() {
      if (!this.animation) return;
      clearTimeout(this.animation.timer);
      this.index = this.animation.index;
      this.animation = null;
      this.renderStage();
      this.commit(true);
      this.processQueue();
    }
    processQueue() {
      if (!this.queuedStep || !this.isConnected) return;
      const step = Math.sign(this.queuedStep);
      this.queuedStep -= step;
      this.go(step);
    }
    cancelPending() {
      this.pendingSettle = null;
      this.loading = false;
      this.removeAttribute('aria-busy');
    }
    commit(announce = false) {
      const photo = this.photos[this.index];
      if (photo.srcset) this.primary.srcset = photo.srcset;
      else this.primary.removeAttribute('srcset');
      this.primary.src = photo.src;
      this.primary.alt = photo.alt || '';
      if (this.card) this.card.toggleAttribute('data-cpg-selected', this.index !== 0);
      this.updatePosition(announce);
    }
    syncSource() {
      const index = this.photos.findIndex(photo => photoKey(photo.src) === photoKey(this.primary.getAttribute('src')));
      if (index === -1 || index === this.index) return;
      this.queuedStep = 0;
      if (this.drag) this.end(null, true);
      this.cancelPending();
      if (this.animation) { clearTimeout(this.animation.timer); this.animation = null; }
      this.index = index;
      this.deactivate();
      this.updatePosition();
      this.card?.toggleAttribute('data-cpg-selected', index !== 0);
    }
    deactivate() {
      this.removeAttribute('data-cpg-engaged');
      this.track?.remove();
      this.track = null;
    }
    disconnectedCallback() {
      this.hideCounter();
      this.controller?.abort();
      this.controller = null;
      this.cancelPending();
      this.pointerController?.abort();
      this.sourceObserver?.disconnect();
      this.resizeObserver?.disconnect();
      if (this.animation) clearTimeout(this.animation.timer);
      this.cache?.forEach(record => record.cancel?.());
      this.cache = null;
      this.drag = this.animation = null;
      this.deactivate();
    }
  });
})();
